import { useMemo, useSyncExternalStore } from "react";
import {
  buildEtoSeries,
  buildEtoSeriesIdw,
  buildSeriesFromStations,
  buildSeriesFromStationsIdw,
  type StationSelectionMethod,
} from "../../model/aemet/aemet.ts";
import { aemetJobStore } from "../../model/aemet/aemetJobStore.ts";
import { deriveRangeInfo } from "../../model/aemet/aemetRangeMath.ts";
import type { LatLon } from "../../model/geo.ts";
import { buildRocioEtoSeries, buildRocioSeries } from "../../model/rocio.ts";
import type { ProjectSeries } from "../../model/dailySeries.ts";

/**
 * Thin view over one sub-basin's job in `aemetJobStore` — the actual
 * download/apply logic lives there now (module-level, not tied to any
 * component), precisely so it survives navigating away from
 * EditSubBasinDialog.tsx entirely, not just closing the small
 * AemetFetchDialog.tsx modal (see the store's own header comment, and
 * AemetFetchDialogHost.tsx, which is what actually renders that modal —
 * independent of any EditSubBasinDialog instance). This hook only adds
 * what's cheap to derive per-render and specific to displaying ONE
 * sub-basin's job: the built series for the report
 * (`precipBuilt`/`tempBuilt`/`etoBuilt`, from `downloadResult` + live
 * `series`/centroid) and range bookkeeping shared with the store
 * (`deriveRangeInfo`).
 *
 * Takes `subBasinCode` rather than a full `SubBasin` — `AemetFetchDialogHost`
 * only knows which job is open, not the sub-basin object it belongs to, and
 * doesn't need it: a job already stores its own `subBasinName`/`centroid`
 * once started. `subBasinName`/`centroid` here are ONLY used to start a
 * BRAND NEW job (see `start()` below) — EditSubBasinDialog.tsx, the only
 * caller that ever starts one, passes both; the host passes neither.
 */
export function useAemetFetch({
  subBasinCode,
  subBasinName,
  series,
  centroid,
  geometry,
}: {
  subBasinCode: number;
  subBasinName?: string;
  series: ProjectSeries;
  centroid?: LatLon | null;
  /** The sub-basin's own polygon, for the "rocio" method's point-in-polygon cell selection (aemetJobStore.ts's `resolveCandidates`) — only used to start a BRAND NEW job, same as `centroid`. */
  geometry?: GeoJSON.Geometry | null;
}) {
  const job = useSyncExternalStore(aemetJobStore.subscribe, () =>
    aemetJobStore.getJob(subBasinCode)
  );
  // Once a job exists it carries its own centroid — only fall back to the
  // caller-supplied one (EditSubBasinDialog.tsx, before the job exists yet)
  // for the brief window between "start clicked" and the job actually
  // landing in the store.
  const effectiveCentroid = job?.centroid ?? centroid ?? null;

  const { forcedRange, range, validRange, windowCount, targetDates } = useMemo(
    () => deriveRangeInfo(job, series),
    [job, series]
  );
  // The range actually being downloaded right now — `range` above ignores
  // extend-mode jobs (AemetBulkFetchDialog.tsx's "extend range", which sets
  // `job.extendRange` instead of going through forced/free range at all; see
  // aemetJobStore.ts's own `rangeInfoForJob`). Only used to show download
  // progress against real dates (AemetDownloadProgress.tsx) — everything
  // else here still reads `forcedRange`/`windowCount`/`targetDates` as before.
  const downloadRange = job?.extendRange ?? range;

  const candidates = job?.candidates ?? [];
  const candidateCodes = candidates.map((c) => c.code);
  const downloadResult = job?.downloadResult ?? null;
  const method = job?.method ?? "nearest";

  // "rocio" aggregates by mean/min/max instead of blending by distance —
  // see the matching comment in aemetJobStore.ts's applyResult (same
  // reasoning applies here, for the live report shown before Apply — MUST
  // stay in sync with applyResult's own logic, since that's what actually
  // gets written to the project).
  const precipBuilt = downloadResult
    ? method === "rocio"
      ? buildRocioSeries(
          candidateCodes,
          downloadResult.byStationByDate,
          targetDates,
          "prec",
          "mean"
        )
      : method === "idw"
        ? buildSeriesFromStationsIdw(
            candidates,
            downloadResult.byStationByDate,
            targetDates,
            "prec"
          )
        : buildSeriesFromStations(
            candidateCodes,
            downloadResult.byStationByDate,
            targetDates,
            "prec"
          )
    : null;
  const tempBuilt = downloadResult
    ? method === "rocio"
      ? buildRocioSeries(candidateCodes, downloadResult.byStationByDate, targetDates, "tmin", "min")
      : method === "idw"
        ? buildSeriesFromStationsIdw(
            candidates,
            downloadResult.byStationByDate,
            targetDates,
            "tmed"
          )
        : buildSeriesFromStations(
            candidateCodes,
            downloadResult.byStationByDate,
            targetDates,
            "tmed"
          )
    : null;
  const etoBuilt =
    downloadResult && effectiveCentroid
      ? method === "rocio"
        ? buildRocioEtoSeries(
            candidateCodes,
            downloadResult.byStationByDate,
            targetDates,
            effectiveCentroid.lat
          )
        : method === "idw"
          ? buildEtoSeriesIdw(
              candidates,
              downloadResult.byStationByDate,
              targetDates,
              effectiveCentroid.lat
            )
          : buildEtoSeries(
              candidateCodes,
              downloadResult.byStationByDate,
              targetDates,
              effectiveCentroid.lat
            )
      : null;

  function stationLabel(code: string): string {
    return candidates.find((c) => c.code === code)?.name ?? code;
  }

  return {
    phase: job?.phase ?? "idle",
    candidates,
    laneCount: job?.laneCount ?? 1,
    errorMessage: job?.errorMessage ?? "",
    forcedRange,
    freeStart: job?.freeStart ?? "",
    setFreeStart: (value: string) => aemetJobStore.setFreeStart(subBasinCode, value),
    freeEnd: job?.freeEnd ?? "",
    setFreeEnd: (value: string) => aemetJobStore.setFreeEnd(subBasinCode, value),
    method,
    setMethod: (value: StationSelectionMethod) => aemetJobStore.setMethod(subBasinCode, value),
    validRange,
    windowCount,
    targetDates,
    downloadRange,
    downloadDone: job?.downloadDone ?? 0,
    downloadTotal: job?.downloadTotal ?? 0,
    downloadResult,
    /** Set only for AemetBulkFetchDialog.tsx's "extend range" mode — lets a row tell "this job is from MY action" apart from a stale one left over from a different mode on the same sub-basin (see its own AemetBulkRow). */
    extendRange: job?.extendRange ?? null,
    retrying: job?.retrying ?? false,
    applyErrorMessage: job?.applyErrorMessage ?? "",
    start: () => {
      if (centroid && subBasinName) {
        aemetJobStore.start(subBasinCode, subBasinName, centroid, { geometry });
      }
    },
    startDownload: () => aemetJobStore.startDownload(subBasinCode),
    cancelDownload: () => aemetJobStore.cancelDownload(subBasinCode),
    retryApply: () => aemetJobStore.retryApply(subBasinCode),
    retryFailedWindows: () => aemetJobStore.retryFailedWindows(subBasinCode),
    dismiss: () => aemetJobStore.dismiss(subBasinCode),
    openDialog: () => aemetJobStore.openDialog(subBasinCode),
    stationLabel,
    precipBuilt,
    tempBuilt,
    etoBuilt,
  };
}

export type AemetFetchState = ReturnType<typeof useAemetFetch>;
